import React, { useState, useEffect, useCallback } from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { Batiment, Personnel, Vehicule } from '../../../shared/types/game';
import BuildingInfoTab from './BuildingDetailModal/BuildingInfoTab';
import BuildingPersonnelTab from './BuildingDetailModal/BuildingPersonnelTab';
import BuildingVehiculesTab from './BuildingDetailModal/BuildingVehiculesTab';
import BuildingDetailsTab from './BuildingDetailModal/BuildingDetailsTab';
import BuildingDetailsCTATab from './BuildingDetailModal/BuildingDetailsCTATab';
import BuildingCellulesTab from './BuildingDetailModal/BuildingCellulesTab';
import BuildingLitsTab from './BuildingDetailModal/BuildingLitsTab';
import BuildingCTATab from './BuildingDetailModal/BuildingCTATab';
import { useBuildingCapacity } from './BuildingDetailModal/useBuildingCapacity';
import {
  getBuildingIconPath,
  getBuildingIconConfig,
  fetchBuildingPersonnel,
  fetchBuildingVehicules,
} from './BuildingDetailModal/buildingDetailHelpers';

export interface BuildingDetailModalProps {
  building: Batiment;
  address?: string;
  onClose: () => void;
  onBuildingUpdate?: (updatedBuilding: Batiment) => void;
}

const BuildingDetailModal: React.FC<BuildingDetailModalProps> = ({ building, address, onClose, onBuildingUpdate }) => {
  const { t } = usePageTranslation('game', 'Map');
  const [activeTab, setActiveTab] = useState<'info' | 'personnel' | 'vehicules' | 'cellules' | 'lits' | 'details' | 'cta'>('info');
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [vehicules, setVehicules] = useState<Vehicule[]>([]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [loadingVehicules, setLoadingVehicules] = useState(false);
  const [buildingData, setBuildingData] = useState<Batiment>(building);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(building.nom);

  const handleBuildingUpdate = useCallback(
    (updated: Batiment) => {
      setBuildingData(updated);
      onBuildingUpdate?.(updated);
    },
    [onBuildingUpdate]
  );

  const {
    calculatedLevel,
    totalCapacity,
    totalVehiclesCapacity,
    timeRemainingPersonnel,
    timeRemainingVehicules,
    handleStartAugmentation
  } = useBuildingCapacity(buildingData, setBuildingData, handleBuildingUpdate);

  // Fonction pour sauvegarder les modifications du nom
  const handleSaveName = () => {
    const updated = {
      ...buildingData,
      nom: editedName,
      updated_at: new Date().toISOString()
    };
    handleBuildingUpdate(updated);
    setIsEditingName(false);
  };

  // Obtenir le chemin de l'icône pour le bâtiment
  const serviceIconPath = getBuildingIconPath(buildingData.type);

  // Charger le personnel et les véhicules associés au bâtiment
  useEffect(() => {
    const loadPersonnel = async () => {
      setLoadingPersonnel(true);
      try {
        const list = await fetchBuildingPersonnel(buildingData.id);
        if (list) setPersonnel(list);
      } catch (error) {
        console.error('Erreur lors du chargement du personnel:', error);
      } finally {
        setLoadingPersonnel(false);
      }
    };

    const loadVehicules = async () => {
      setLoadingVehicules(true);
      try {
        const list = await fetchBuildingVehicules(buildingData.id);
        if (list) setVehicules(list);
      } catch (error) {
        console.error('Erreur lors du chargement des véhicules:', error);
      } finally {
        setLoadingVehicules(false);
      }
    };

    loadPersonnel();
    loadVehicules();
  }, [buildingData.id]);

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-zinc-950 rounded-xl shadow-2xl w-full max-w-5xl max-h-[88vh] overflow-hidden border border-zinc-800/90 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 flex items-center gap-4 px-5 py-4 border-b border-zinc-800/80 bg-zinc-900/60">
          <div
            className="rounded-lg flex items-center justify-center border border-white/10 shadow-lg shrink-0"
            style={{
              backgroundColor: getBuildingIconConfig(buildingData.type).backgroundColor,
              width: '42px',
              height: '42px',
            }}
          >
            <img src={serviceIconPath} alt={buildingData.type} className="w-7 h-7" />
          </div>

          <div className="flex-1 min-w-0">
            {isEditingName ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onBlur={handleSaveName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  else if (e.key === 'Escape') { setEditedName(buildingData.nom); setIsEditingName(false); }
                }}
                className="text-lg font-bold bg-zinc-800 text-white px-3 py-1 rounded-lg border border-sky-500/60 focus:outline-none focus:ring-2 focus:ring-sky-500/40 w-full max-w-md"
                autoFocus
              />
            ) : (
              <button
                type="button"
                className="group flex items-center gap-2 text-left"
                onClick={() => setIsEditingName(true)}
                title="Cliquer pour renommer"
              >
                <h2 className="text-lg font-bold text-white group-hover:text-sky-300 transition-colors leading-tight truncate">
                  {buildingData.nom}
                </h2>
                <svg className="w-3.5 h-3.5 text-zinc-500 group-hover:text-sky-400 transition-colors shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
            <p className="text-xs text-zinc-500 mt-0.5">
              {t(`contextMenu.buildingTypes.${buildingData.type}`)}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
            aria-label="Fermer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="shrink-0 flex overflow-x-auto border-b border-zinc-800/80 bg-zinc-900/40 scrollbar-none">
          {(
            [
              { key: 'info', label: 'Informations', show: true, count: null },
              { key: 'personnel', label: 'Personnel', show: buildingData.type !== 'cta', count: personnel.length > 0 ? personnel.length : null },
              { key: 'vehicules', label: 'Véhicules', show: buildingData.type !== 'cta', count: vehicules.length > 0 ? vehicules.length : null },
              { key: 'cellules', label: 'Cellules', show: buildingData.type === 'prison' || buildingData.type === 'fdo', count: (buildingData.capacite || 0) > 0 ? (buildingData.capacite || 0) : null },
              { key: 'lits', label: 'Chambres', show: buildingData.type === 'hopital', count: (buildingData.capacite || 0) > 0 ? (buildingData.capacite || 0) : null },
              { key: 'cta', label: 'Bâtiments liés', show: buildingData.type === 'cta', count: null },
              { key: 'details', label: 'Détails', show: true, count: null },
            ] as { key: typeof activeTab; label: string; show: boolean; count: number | null }[]
          ).filter((tab) => tab.show).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap px-4 py-3 text-xs font-semibold transition-colors border-b-2 ${
                activeTab === tab.key
                  ? 'border-sky-500 text-sky-300 bg-zinc-800/40'
                  : 'border-transparent text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/20'
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${
                  activeTab === tab.key ? 'bg-sky-500/20 text-sky-300' : 'bg-zinc-700/80 text-zinc-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 bg-zinc-950/50">
          {activeTab === 'info' && (
            <BuildingInfoTab
              building={buildingData}
              address={address}
              calculatedLevel={calculatedLevel}
              totalCapacity={totalCapacity}
              totalVehiclesCapacity={totalVehiclesCapacity}
              timeRemainingPersonnel={timeRemainingPersonnel}
              timeRemainingVehicules={timeRemainingVehicules}
              handleStartAugmentation={handleStartAugmentation}
              onBuildingUpdate={handleBuildingUpdate}
            />
          )}

          {activeTab === 'personnel' && buildingData.type !== 'cta' && (
            <BuildingPersonnelTab
              personnel={personnel}
              loadingPersonnel={loadingPersonnel}
              buildingType={buildingData.type}
              buildingId={buildingData.id}
              buildingName={buildingData.nom}
              userId={buildingData.user_id}
              onPersonnelAdded={() => {
                (async () => {
                  setLoadingPersonnel(true);
                  try {
                    const list = await fetchBuildingPersonnel(buildingData.id);
                    if (list) setPersonnel(list);
                  } catch (error) {
                    console.error('Erreur lors du rechargement du personnel:', error);
                  } finally {
                    setLoadingPersonnel(false);
                  }
                })();
              }}
            />
          )}

          {activeTab === 'vehicules' && buildingData.type !== 'cta' && (
            <BuildingVehiculesTab
              vehicules={vehicules}
              loadingVehicules={loadingVehicules}
              buildingType={buildingData.type}
              buildingId={buildingData.id}
              userId={buildingData.user_id}
              onVehiculeAdded={() => {
                (async () => {
                  setLoadingVehicules(true);
                  try {
                    const list = await fetchBuildingVehicules(buildingData.id);
                    if (list) setVehicules(list);
                  } catch (error) {
                    console.error('Erreur lors du rechargement des véhicules:', error);
                  } finally {
                    setLoadingVehicules(false);
                  }
                })();
              }}
            />
          )}

          {activeTab === 'cellules' && (
            <BuildingCellulesTab
              building={buildingData}
              onIncreaseCapacity={buildingData.type === 'prison' ? () => handleStartAugmentation('personnel', 1) : undefined}
            />
          )}

          {activeTab === 'lits' && (
            <BuildingLitsTab
              building={buildingData}
            />
          )}

          {activeTab === 'cta' && buildingData.type === 'cta' && (
            <BuildingCTATab
              ctaBuilding={buildingData}
              onBuildingUpdate={handleBuildingUpdate}
            />
          )}

          {activeTab === 'details' && buildingData.type === 'cta' && (
            <BuildingDetailsCTATab ctaBuilding={buildingData} />
          )}
          {activeTab === 'details' && buildingData.type !== 'cta' && (
            <BuildingDetailsTab
              building={buildingData}
              personnel={personnel}
              vehicules={vehicules}
              calculatedLevel={calculatedLevel}
              totalCapacity={totalCapacity}
              totalVehiclesCapacity={totalVehiclesCapacity}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildingDetailModal;

